import React from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Userpilot } from 'userpilot';

import { ApplicationParams } from 'types/route_params';

import { useAccessTokens } from 'hooks/api/useAccessTokens';
import { useAccount } from 'hooks/api/useAccount';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useApplication } from 'application/hooks/useApplication';
import { useFilesSearchClient } from 'files/hooks/useFilesSearchClient';
import { useHasAccess } from 'hooks/useHasAccess';
import { useIsLumeoAdmin } from 'hooks/useAuth';
import { updateGleapCustomData } from 'services/gleap';

import { Admin } from 'application/views/Admin/Admin';
import { Overview } from 'application/views/Overview/Overview';
import { Deploy } from 'application/views/Deploy/Deploy';
import { Design } from 'application/views/Design/Design';
import { Monitor } from 'application/views/Monitor/Monitor';
import { Settings } from 'application/views/Settings/Settings';

import { Button } from 'components/Button/Button';
import { Heading } from 'components/Heading/Heading';
import { RequireAuth } from 'components/RequireAuth/RequireAuth';
import { Text } from 'components/Text/Text';

export function App() {
  const isAdmin = useIsLumeoAdmin();
  const [hasAccess] = useHasAccess();

  const location = useLocation();

  const { applicationID } = useParams<ApplicationParams>();
  const { data: application } = useApplication(applicationID);
  const { data: account } = useAccount();
  const { data: organization } = useCurrentOrganization();

  const accountId = account?.id;
  const applicationName = application?.name;
  const appRoles = account?.roles
    .filter(
      ({ application_id, organization_id }) =>
        application_id === application?.id ||
        organization_id === application?.organization_id
    )
    .map(({ name }) => name);
  const isBilling = appRoles?.some((name) => name === 'billing');
  const roles = appRoles?.join(',');

  // Pre-load
  useAccessTokens();
  useFilesSearchClient();

  React.useEffect(() => {
    Userpilot.reload();
  }, [location]);

  React.useEffect(() => {
    if (!accountId) {
      return;
    }

    const traits = {
      applicationID,
      applicationName,
      isBilling,
      roles,
    };

    Userpilot.identify(accountId, traits);
    updateGleapCustomData(traits);
  }, [applicationID, applicationName, accountId, isBilling, roles]);

  if (!application) {
    return null;
  }

  if (organization?.is_suspended) {
    return (
      <Routes>
        {/* SETTINGS */}
        <Route path="settings/*" element={<Settings />} />

        {/* ADMIN */}
        {/* Not handled as a private route so we can pretend it's a 404 */}
        {isAdmin && <Route path="admin/*" element={<Admin />} />}

        <Route
          path="*"
          element={
            <Navigate to={`/applications/${applicationID}/settings`} replace />
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* OVERVIEW */}
      {hasAccess('overview') && (
        <Route
          path="*"
          element={
            <RequireAuth>
              <Overview />
            </RequireAuth>
          }
        />
      )}

      {!hasAccess('overview') &&
        (hasAccess('monitor') ? (
          <Route path="*" element={<Navigate to="../monitor" replace />} />
        ) : (
          <Route path="*" element={<Navigate to="../settings" replace />} />
        ))}

      {/* DESIGN */}
      <Route
        path="design/*"
        element={
          <RequireAuth section="design">
            <Design />
          </RequireAuth>
        }
      />

      {/* DEPLOY */}
      <Route
        path="deploy/*"
        element={
          <RequireAuth section="deploy_edit">
            <Deploy />
          </RequireAuth>
        }
      />

      {/* MONITOR */}
      <Route
        path="monitor/*"
        element={
          <RequireAuth section="monitor">
            <Monitor />
          </RequireAuth>
        }
      />

      {/* SETTINGS */}
      <Route path="settings/*" element={<Settings />} />

      {/* ADMIN */}
      {/* Not handled as a private route so we can pretend it's a 404 */}
      {isAdmin && <Route path="admin/*" element={<Admin />} />}

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="error-screen error-message">
            <div className="error-message-content">
              <Text>404</Text>
              <Heading level="1">Page not found.</Heading>
              <p></p>
              <Button to="/">Go to dashboard</Button>
            </div>
          </div>
        }
      />
    </Routes>
  );
}
